import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "./components/ui";
import { Loader2, RefreshCw, PackageCheck } from "lucide-react";
import { useLanguage } from "./LanguageContext";

const resolveApiBase = () => {
  const env = process.env.REACT_APP_API_URL;
  if (env && env.trim().length > 0) {
    const trimmed = env.trim().replace(/\/+$/, "");
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  }
  if (typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")) {
    return "http://127.0.0.1:8000";
  }
  return "/api";
};
const API_URL = resolveApiBase();

const BASE_RETURNS_COPY = {
  pageTitle: "Managed Returns",
  pageSubtitle: "Source: public.managed_returns",
  errorTitle: "We couldn’t fetch the latest returns",
  filtersTitle: "Filters",
  filtersDescription: "Search returns by order, user or time range.",
  apply: "Apply",
  loading: "Loading",
  clear: "Clear",
  orderIdLabel: "Order ID",
  orderIdPlaceholder: "Contains...",
  addedByLabel: "Added by",
  addedByPlaceholder: "Contains...",
  userIdLabel: "User ID",
  userIdPlaceholder: "Exact",
  fromLabel: "From",
  toLabel: "To",
  resultsTitle: (count) => `Results (${count})`,
  resultsDescription: "Showing managed returns in the selected range.",
  pageSizeLabel: "Page size",
  loadingTable: "Loading managed returns...",
  emptyTitle: "No results",
  emptyDescription: "Try adjusting the filters above and refresh.",
  tableHeaders: {
    orderId: "Order ID",
    userId: "User ID",
    addedBy: "Added By",
    createdAt: "Created At"
  }
};

const RETURNS_TRANSLATIONS = {
  en: BASE_RETURNS_COPY,
  tr: {
    ...BASE_RETURNS_COPY,
    pageTitle: "Yönetilen iadeler",
    pageSubtitle: "Kaynak: public.managed_returns",
    errorTitle: "En son iadeler getirilemedi",
    filtersTitle: "Filtreler",
    filtersDescription: "Siparişe, kullanıcıya veya tarih aralığına göre iadeleri arayın.",
    apply: "Uygula",
    loading: "Yükleniyor",
    clear: "Temizle",
    orderIdLabel: "Sipariş ID",
    orderIdPlaceholder: "İçerir...",
    addedByLabel: "Ekleyen",
    addedByPlaceholder: "İçerir...",
    userIdLabel: "Kullanıcı ID",
    userIdPlaceholder: "Tam",
    fromLabel: "Başlangıç",
    toLabel: "Bitiş",
    resultsTitle: (count) => `Sonuçlar (${count})`,
    resultsDescription: "Seçilen aralıkta yönetilen iadeler gösteriliyor.",
    pageSizeLabel: "Sayfa boyutu",
    loadingTable: "Yönetilen iadeler yükleniyor...",
    emptyTitle: "Sonuç yok",
    emptyDescription: "Yukarıdaki filtreleri değiştirip yeniden deneyin.",
    tableHeaders: {
      orderId: "Sipariş ID",
      userId: "Kullanıcı ID",
      addedBy: "Ekleyen",
      createdAt: "Oluşturulma"
    }
  }
};

const Returns = ({ token }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ order_id: "", added_by: "", user_id: "", created_from: "", created_to: "" });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const { language } = useLanguage();
  const translations = RETURNS_TRANSLATIONS[language] || RETURNS_TRANSLATIONS.en;

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchReturns = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      params.append("limit", String(pageSize));
      params.append("offset", String(page * pageSize));
      const res = await axios.get(`${API_URL}/returns/`, { headers, params });
      setRows(res.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load returns");
    } finally {
      setLoading(false);
    }
  }, [headers, filters, page, pageSize]);

  useEffect(() => {
    fetchReturns();
  }, [fetchReturns]);

  const onFilterChange = (event) => setFilters((current) => ({ ...current, [event.target.name]: event.target.value }));
  const clearFilters = () => setFilters({ order_id: "", added_by: "", user_id: "", created_from: "", created_to: "" });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-foreground">{translations.pageTitle}</h1>
        <p className="text-sm text-muted-foreground">{translations.pageSubtitle}</p>
      </div>

      {error && (
        <Alert variant="destructive" className="border-destructive/40 bg-destructive/10">
          <AlertTitle>{translations.errorTitle}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card className="border-border/60 bg-card/70">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">{translations.filtersTitle}</CardTitle>
            <CardDescription>{translations.filtersDescription}</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-2" onClick={fetchReturns} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {loading ? translations.loading : translations.apply}
            </Button>
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              {translations.clear}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-5">
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {translations.orderIdLabel}
              </label>
              <Input
                className="w-full"
                name="order_id"
                placeholder={translations.orderIdPlaceholder}
                value={filters.order_id}
                onChange={onFilterChange}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {translations.addedByLabel}
              </label>
              <Input
                name="added_by"
                placeholder={translations.addedByPlaceholder}
                value={filters.added_by}
                onChange={onFilterChange}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {translations.userIdLabel}
              </label>
              <Input
                name="user_id"
                placeholder={translations.userIdPlaceholder}
                value={filters.user_id}
                onChange={onFilterChange}
              />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {translations.fromLabel}
              </label>
              <Input type="datetime-local" name="created_from" value={filters.created_from} onChange={onFilterChange} />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {translations.toLabel}
              </label>
              <Input type="datetime-local" name="created_to" value={filters.created_to} onChange={onFilterChange} />
        </div>
      </div>
        </CardContent>
      </Card>

      <Card className="border-border/60 bg-card/70">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">{translations.resultsTitle(rows.length)}</CardTitle>
            <CardDescription>{translations.resultsDescription}</CardDescription>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <label htmlFor="pageSize">{translations.pageSizeLabel}</label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={(event) => {
                setPageSize(parseInt(event.target.value, 10));
                setPage(0);
              }}
              className="rounded-md border border-border/60 bg-background px-2 py-1 text-sm"
            >
              {[25, 50, 100, 200].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span>{translations.loadingTable}</span>
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center text-muted-foreground">
              <PackageCheck className="h-10 w-10" />
              <div className="space-y-1">
                <p className="text-base font-medium text-foreground">{translations.emptyTitle}</p>
                <p className="text-sm">{translations.emptyDescription}</p>
              </div>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-border/60">
              <Table>
                <TableHeader className="bg-muted/40">
                  <TableRow>
                    <TableHead>{translations.tableHeaders.orderId}</TableHead>
                    <TableHead>{translations.tableHeaders.userId}</TableHead>
                    <TableHead>{translations.tableHeaders.addedBy}</TableHead>
                    <TableHead>{translations.tableHeaders.createdAt}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow key={`${row.order_id}-${index}`} className="hover:bg-muted/40">
                      <TableCell className="font-mono text-sm font-semibold text-foreground">{row.order_id}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{row.user_id}</TableCell>
                      <TableCell className="text-sm text-foreground">{row.added_by}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(row.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Returns;


